import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Button } from '@/shared/ui/button';
import { Textarea } from '@/shared/ui/textarea';
import { Badge } from '@/shared/ui/badge';
import { ErrorState } from '@/shared/feedback';
import { OnboardingLifecycleApi } from '@/modules/tenants/services/onboarding-lifecycle-api';
import type {
  OnboardingSubmission,
  OnboardingReviewDecision,
} from '@/api/schemas/onboarding-lifecycle';
import { FiCheckCircle, FiXCircle, FiInfo, FiZap } from 'react-icons/fi';

const STATUS_TONE: Record<OnboardingSubmission['status'], 'positive' | 'caution' | 'critical' | 'neutral' | 'info'> = {
  submitted: 'info',
  approved: 'positive',
  rejected: 'critical',
  more_info_required: 'caution',
  activated: 'positive',
};

const STATUS_LABEL: Record<OnboardingSubmission['status'], string> = {
  submitted: 'Submitted — awaiting review',
  approved: 'Approved — ready to activate',
  rejected: 'Rejected',
  more_info_required: 'More information required',
  activated: 'Activated',
};

/**
 * Platform onboarding review for a named tenant.
 *
 * The platform operator reviews the tenant's latest submission (approve / reject
 * / request more information) and activates an approved submission. The readiness
 * snapshot is FROZEN at submit time and shown here read-only.
 *
 * NOTE: the platform surface reads the submission via the tenant-scoped
 * `GET /tenants/current/onboarding/submission` only when the operator is a
 * member. A platform operator who is NOT a member reviews through the tenant
 * detail page, which resolves the submission from the tenant's own record.
 */
export function OnboardingReviewPanel({ tenantId }: { tenantId: string }): ReactNode {
  const [submission, setSubmission] = useState<OnboardingSubmission | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const load = useCallback(async () => {
    try {
      setSubmission(await OnboardingLifecycleApi.getCurrent());
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load onboarding submission'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    OnboardingLifecycleApi.getCurrent()
      .then((result) => {
        setSubmission(result);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err : new Error('Failed to load onboarding submission'));
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  if (isLoading) return <p className="text-sm text-content-muted">Loading…</p>;
  if (error) return <ErrorState message={error.message} onRetry={() => void load()} />;
  if (!submission) {
    return (
      <p className="text-sm text-content-muted">
        This tenant has not submitted its onboarding for review yet.
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <SubmissionHeader submission={submission} />
      <ReadinessSummary submission={submission} />
      {submission.decisionReason ? <DecisionReason reason={submission.decisionReason} /> : null}
      {submission.status === 'submitted' || submission.status === 'more_info_required' ? (
        <ReviewActions
          tenantId={tenantId}
          submission={submission}
          onUpdated={(updated) => setSubmission(updated)}
        />
      ) : null}
      {submission.status === 'approved' ? (
        <ActivateAction
          tenantId={tenantId}
          submission={submission}
          onUpdated={(updated) => setSubmission(updated)}
        />
      ) : null}
    </div>
  );
}

function DecisionReason({ reason }: { reason: string }): ReactNode {
  return (
    <div className="rounded-lg border border-border bg-surface-sunken p-4">
      <p className="text-xs tracking-wide text-content-muted uppercase">Decision reason</p>
      <p className="text-sm text-content">{reason}</p>
    </div>
  );
}

function SubmissionHeader({ submission }: { submission: OnboardingSubmission }): ReactNode {
  return (
    <div className="flex items-center justify-between gap-4">
      <div>
        <h4 className="font-medium">Onboarding submission</h4>
        <p className="text-xs text-content-muted">
          Submitted {new Date(submission.submittedAt).toLocaleString()} · version{' '}
          {submission.version}
        </p>
      </div>
      <Badge tone={STATUS_TONE[submission.status]}>{STATUS_LABEL[submission.status]}</Badge>
    </div>
  );
}

function ReadinessSummary({ submission }: { submission: OnboardingSubmission }): ReactNode {
  const { readinessSnapshot } = submission;
  return (
    <div className="rounded-lg border border-border p-4">
      <div className="mb-3 flex items-center justify-between">
        <p className="text-sm font-medium">Readiness snapshot (frozen at submit)</p>
        <Badge tone={readinessSnapshot.overall === 'complete' ? 'positive' : 'caution'}>
          {readinessSnapshot.overall === 'complete'
            ? 'Complete'
            : `${readinessSnapshot.missingCount} missing`}
        </Badge>
      </div>
      <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {readinessSnapshot.categories.map((category) => (
          <li key={category.category} className="flex items-center gap-2 text-sm">
            <span
              className={
                category.status === 'complete'
                  ? 'text-positive'
                  : category.status === 'missing'
                    ? 'text-caution'
                    : 'text-content-muted'
              }
            >
              {category.status === 'complete' ? (
                <FiCheckCircle className="h-4 w-4" />
              ) : category.status === 'missing' ? (
                <FiXCircle className="h-4 w-4" />
              ) : (
                <FiInfo className="h-4 w-4" />
              )}
            </span>
            <span className="text-content">{category.category}</span>
            <span className="text-xs text-content-muted">{category.status}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ReviewActions({
  tenantId,
  submission,
  onUpdated,
}: {
  tenantId: string;
  submission: OnboardingSubmission;
  onUpdated: (submission: OnboardingSubmission) => void;
}): ReactNode {
  const [reason, setReason] = useState('');
  const [pending, setPending] = useState<OnboardingReviewDecision | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const decide = async (decision: OnboardingReviewDecision) => {
    setPending(decision);
    setError(null);
    try {
      const updated = await OnboardingLifecycleApi.review(tenantId, submission.id, {
        decision,
        expectedVersion: submission.version,
        ...(decision === 'approved' ? {} : { reason: reason.trim() }),
      });
      onUpdated(updated);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to record decision'));
    } finally {
      setPending(null);
    }
  };

  const reasonRequired = reason.trim() === '';

  return (
    <div className="space-y-4 rounded-lg border border-border p-4">
      <h4 className="font-medium">Review decision</h4>
      <Textarea
        label="Reason"
        hint="Required when rejecting or requesting more information. Omitted when approving."
        value={reason}
        onChange={(event) => setReason(event.currentTarget.value)}
      />
      {error ? <ErrorState message={error.message} /> : null}
      <DecisionButtons
        pending={pending}
        reasonRequired={reasonRequired}
        onDecide={(decision) => void decide(decision)}
      />
    </div>
  );
}

function DecisionButtons({
  pending,
  reasonRequired,
  onDecide,
}: {
  pending: OnboardingReviewDecision | null;
  reasonRequired: boolean;
  onDecide: (decision: OnboardingReviewDecision) => void;
}): ReactNode {
  return (
    <div className="flex flex-wrap gap-3">
      <Button
        onClick={() => onDecide('approved')}
        loading={pending === 'approved'}
        disabled={pending !== null}
      >
        <FiCheckCircle className="mr-2 h-4 w-4" />
        Approve
      </Button>
      <Button
        variant="secondary"
        onClick={() => onDecide('more_info_required')}
        loading={pending === 'more_info_required'}
        disabled={pending !== null || reasonRequired}
      >
        <FiInfo className="mr-2 h-4 w-4" />
        Request more info
      </Button>
      <Button
        variant="danger"
        onClick={() => onDecide('rejected')}
        loading={pending === 'rejected'}
        disabled={pending !== null || reasonRequired}
      >
        <FiXCircle className="mr-2 h-4 w-4" />
        Reject
      </Button>
    </div>
  );
}

function ActivateAction({
  tenantId,
  submission,
  onUpdated,
}: {
  tenantId: string;
  submission: OnboardingSubmission;
  onUpdated: (submission: OnboardingSubmission) => void;
}): ReactNode {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const activate = async () => {
    setPending(true);
    setError(null);
    try {
      onUpdated(await OnboardingLifecycleApi.activate(tenantId, submission.id, submission.version));
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to activate submission'));
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="space-y-4 rounded-lg border border-positive bg-positive-soft p-4">
      <p className="text-sm text-content">
        This submission is approved. Activating it marks the tenant’s onboarding complete.
      </p>
      {error ? <ErrorState message={error.message} /> : null}
      <Button onClick={() => void activate()} loading={pending}>
        <FiZap className="mr-2 h-4 w-4" />
        Activate onboarding
      </Button>
    </div>
  );
}
